import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styles from '../pages/JobDashboard/JobDetailNew.module.css'; // Reusing styles for a consistent look
import { API_BASE_URL } from '../config';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import Loader2 from '../components/loader2';
import showToast from "../components/ShowToast";
import Swal from 'sweetalert2';
import ModernNavbarPureCSS from "../components/Navbar/ModernNavBarPureCSS";


import {
  ArrowLeft,
  Building,
  Link2,
  FileText,
  Camera,
  Briefcase,
  Target,
  ShieldCheck,
  Award,
  Users,
  Upload,
  Plus,
  X,
  Search,
  MapPin,
  Phone,
  Home,
  LogOut
} from "lucide-react";

const BusinessOnboardingNew = () => {
  const navigate = useNavigate();
  const { businessId: businessIdFromParams } = useParams(); 
  const [username, setUsername] = useState('');

  const [businessUserId, setBusinessUserId] = useState(null);
  const [businessId, setBusinessId] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      const decodedToken = jwtDecode(token);
      setBusinessUserId(decodedToken.nameid);
      setBusinessId(decodedToken.BusinessId);
    }
  }, []);

  // --- State for all onboarding fields ---

  // Page 1: Locations
  const [addresses, setAddresses] = useState([]);
  const [currentAddressInput, setCurrentAddressInput] = useState('');
  const [currentMobileInput, setCurrentMobileInput] = useState('');
  const [currentLandlineInput, setCurrentLandlineInput] = useState('');
  const [addressResults, setAddressResults] = useState([]);
  const [isSearchingAddress, setIsSearchingAddress] = useState(false);

  // Page 2: Business Details
  const [businessName, setBusinessName] = useState(localStorage.getItem('businessName') || '');
  const [businessWebsite, setBusinessWebsite] = useState('');
  const [abn, setAbn] = useState('');
  const [acn, setAcn] = useState('');
  const [shortBio, setShortBio] = useState('');
  const [longBio, setLongBio] = useState('');
  const [profileImage, setProfileImage] = useState(null);
  const [profileImageBase64, setProfileImageBase64] = useState(null);
  const [preview, setPreview] = useState(null);


  // Page 3: Scope and Professions
  const [businessType, setBusinessType] = useState('');
  const [scope, setScope] = useState({ residential: false, commercial: false, government: false });
  const [selectedProfessions, setSelectedProfessions] = useState([]);
  const [professionSearchTerm, setProfessionSearchTerm] = useState('');
  const [professionResults, setProfessionResults] = useState([]);
  const [isProfessionDropdownOpen, setIsProfessionDropdownOpen] = useState(false);
  const professionDropdownRef = useRef(null);

  // Page 4: Services
  const [selectedServices, setSelectedServices] = useState([]);
  const [serviceSearchTerm, setServiceSearchTerm] = useState('');
  const [serviceResults, setServiceResults] = useState([]);
  const [professionServicesMap, setProfessionServicesMap] = useState({});
  const [showServicesMessage, setShowServicesMessage] = useState(false);
  const [showAddServicePopup, setShowAddServicePopup] = useState(false);
  const [newService, setNewService] = useState({ name: '', professionId: '' });
  const [addServiceLoading, setAddServiceLoading] = useState(false);

  const isInitialMount = useRef(true);

  const handleAddNewService = async () => {
    if (!newService.name.trim()) {
      showToast('error', 'Please enter a service name.');
      return;
    }
    if (!newService.professionId) {
      showToast('error', 'Please select a profession.');
      return;
    }
    // Basic validation for strange characters
    if (!/^[a-zA-Z0-9\s\-&]+$/.test(newService.name)) {
        showToast('error', 'Service name contains invalid characters.');
        return;
    }

    // Prevent adding a duplicate service
    if (selectedServices.some(s => s.name.toLowerCase() === newService.name.toLowerCase())) {
        showToast('error', 'This service has already been added.');
        return;
    }

    setAddServiceLoading(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/api/Professional/createService`, {
        name: newService.name,
        professionId: newService.professionId,
        description: newService.name, // As requested
      });

      // The backend returns 201 Created on success with the new object
      if (response.status === 201 && response.data && response.data.id) {
        const newlyAddedService = {
          id: response.data.id, // Corrected from serviceId to id
          name: response.data.name,
        };
        setSelectedServices(prev => [...prev, newlyAddedService]);
        showToast('success', 'Service added successfully!');
        setShowAddServicePopup(false);
        setNewService({ name: '', professionId: '' });
      } else {
        showToast('error', 'Failed to add service. Please try again.');
      }
    } catch (error) {
      console.error('Error creating new service:', error);
      if (error.response && error.response.data) {
        // Show specific error from backend if available
        showToast('error', `An error occurred: ${error.response.data}`);
      } else {
        showToast('error', 'An error occurred while adding the service.');
      }
    } finally {
      setAddServiceLoading(false);
    }
  };


  // Page 5: Products
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [productResults, setProductResults] = useState([]);

  // Page 3: Scope and Professions (continued)
  const [serviceAfterHours, setServiceAfterHours] = useState(false);
  const [serviceOnWeekends, setServiceOnWeekends] = useState(false);

  // Page 6: Certifications
  const [licenseNumber, setLicenseNumber] = useState('');
  const [licenseExpiry, setLicenseExpiry] = useState('');
  const [isFullyInsured, setIsFullyInsured] = useState(false);
  const [availableCertifications, setAvailableCertifications] = useState([]);
  const [selectedCertifications, setSelectedCertifications] = useState([]);

  // Page 7: Qualifications
  const [registrationNumber, setRegistrationNumber] = useState('');
  const [yearsExperience, setYearsExperience] = useState(1);
  const [availableQualifications, setAvailableQualifications] = useState([]);
  const [selectedQualifications, setSelectedQualifications] = useState([]);
  const [availableMemberships, setAvailableMemberships] = useState([]);
  const [selectedMemberships, setSelectedMemberships] = useState([]);
  const [document, setDocument] = useState(null);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isReadOnly, setIsReadOnly] = useState(!!businessIdFromParams);

  const handleButtonClick = (action) =>
  {
      if (action === 'logout')
      {
          localStorage.removeItem('token');
          sessionStorage.removeItem('loginSuccessMessage');
          sessionStorage.removeItem('showToast');
          sessionStorage.removeItem('showBusinessToast');
          sessionStorage.removeItem('userImage');
          localStorage.removeItem('businesName');
          window.location.href = '/login';
      }
  };

  // --- Handlers and Effects ---

  // Fetch initial data and business data for editing
  useEffect(() => {
    const fetchData = async () => {

      const token = localStorage.getItem('token');

      //console.log(`localStorage.getItem('token') ${localStorage.getItem('token')}`)

      if (!token) {
        navigate('/login');
        return;
      }
      
      const decodedToken = jwtDecode(token);
      const loggedInUserId = decodedToken.nameid;
      const userType = decodedToken.UserType;

      if (userType != 'Business') {
        showToast('error', 'You are not authorized to access this page.');
        navigate('/login');
        return;
      }

      setBusinessUserId(loggedInUserId);
      setBusinessId(decodedToken.BusinessId);

      setIsLoading(true);

      console.log(`BUID = ${loggedInUserId}`);
      console.log(`BID = ${decodedToken.BusinessId}`);

      try {

        // Fetch dropdown data OLD - DEFUNCT
/*
        const [certs, quals, memberships] = await Promise.all([
          axios.get(`${API_BASE_URL}/api/BusinessOnboarding/certifications?businessUserId=${loggedInUserId}`),
          axios.get(`${API_BASE_URL}/api/BusinessOnboarding/qualifications?businessId=${decodedToken.BusinessId}`),
          axios.get(`${API_BASE_URL}/api/BusinessOnboarding/memberships?businessId=${decodedToken.BusinessId}`)
        ]);
        setAvailableCertifications(certs.data);
        setAvailableQualifications(quals.data);
        setAvailableMemberships(memberships.data);

*/

        // If in edit mode, fetch the business profile data
        if (businessIdFromParams) {
          if (businessIdFromParams === String(decodedToken.BusinessId)) {
            setIsReadOnly(false);
          } else {
            showToast('error', "You can only edit your own profile.");
            setIsReadOnly(true);
          }
          
          // Fetch and populate data for the form...
          const profileResponse = await axios.get(`${API_BASE_URL}/api/BusinessProfile/getBusinessProfile?businessId=${businessIdFromParams}`);
          //console.log('Retrieved Business Profile');

          const locationsResponse = await axios.get(`${API_BASE_URL}/api/BusinessProfile/getBusinessLocations?businessId=${businessIdFromParams}`);
          //console.log('Retrieved Business Locations');

          if (profileResponse.data && profileResponse.data.businesses) {
            const businessData = profileResponse.data.businesses;
            setBusinessName(businessData.businessName);
            setBusinessWebsite(businessData.website);
            setAbn(businessData.abn);
            setAcn(businessData.acn);
            setShortBio(businessData.description);
            setLongBio(businessData.profileDescription);
            setBusinessType(businessData.businessType || '');
            setScope({
              residential: businessData.residential === 1,
              commercial: businessData.commercial === 1,
              government: businessData.government === 1,
            });
            setServiceAfterHours(businessData.afterHours === 1);
            setServiceOnWeekends(businessData.weekends === 1);
            setLicenseNumber(businessData.licenseNumber);
            setLicenseExpiry(businessData.formattedLicenseExpiry);
            setIsFullyInsured(businessData.fullyInsured);
            setRegistrationNumber(businessData.registrationNumber);
            setYearsExperience(businessData.yearsExperience);
            //setPreview(businessData.profilePic);

            if (businessData.profilePic) {
              setPreview(businessData.profilePic);
              setProfileImageBase64(businessData.profilePic); 
            }
            
            setSelectedProfessions(profileResponse.data.professions || []);
            setSelectedServices(profileResponse.data.services || []);
            setSelectedProducts(
              (profileResponse.data.products || []).map(p => ({
                id: p.productId,
                name: p.productName,
              }))
            );

            console.log(profileResponse.data.certifications);
            setSelectedCertifications(profileResponse.data.certifications.map(c => c.certificationId) || []);
            setSelectedQualifications(profileResponse.data.qualificationDtos.map(q => q.qualificationId) || []);
          }

          if (locationsResponse.data) {
            setAddresses(locationsResponse.data.map(l => ({
              address: `${l.streetNumber} ${l.streetName}, ${l.suburb}, ${l.myState} ${l.postcode}`,
              mobile: l.phone,
              landline: l.landline,
              isPrimary: l.isPrimary === 1
            })));
          }
        }

        // After the initial data is successfully loaded and set, we mark the initial mount as complete.
        isInitialMount.current = false;

      } catch (err) {
        showToast('error', 'Failed to load initial data.');
        console.error(err);
      }
      setIsLoading(false);
    };
    fetchData();
  }, [businessIdFromParams, navigate]);



  // fetch drop down data based on the professions selected.
  useEffect(() => {
    // A function to fetch data based on the currently selected professions
    const fetchProfessionDependentData = async () => {


      // --- FIX ---
      // This logic now runs ONLY if it's NOT the initial data load.
      // If the user manually changes professions later, this will correctly clear the old selections.
      if (!isInitialMount.current) {
        // If professions change, it's safest to clear previous selections.
        //setSelectedCertifications([]);
        //setSelectedQualifications([]);
        //setSelectedMemberships([]);
        //showToast('info', 'Certification and qualification lists have been updated based on your professions.');
      }
      // --- END FIX ---


      // If no professions are selected, clear the lists and do nothing.
      if (selectedProfessions.length === 0) {

        setAvailableCertifications([]); // Explicitly clear available options too
        setAvailableQualifications([]);
        setAvailableMemberships([]);

        return;
      }

      // Inform the user that the lists have been updated.
      showToast('info', 'Certification and qualification lists have been updated based on your professions.');

      setIsLoading(true); // Show loader while fetching new options

      // Extract the IDs from the selected professions
      const professionIds = selectedProfessions.map(p => p.id || p.professionId);

      const params = new URLSearchParams();
      professionIds.forEach(id => params.append('professionIds', id));
      const queryString = params.toString();

      try {
        // Fetch all dependent data in parallel
        //console.log(`CERTS-BY-PROF URL = ${API_BASE_URL}/api/BusinessOnboarding/certifications-by-professions?professionIds=${queryString}`);


        const [certs, quals, memberships] = await Promise.all([
          // Pass the profession IDs as a query parameter
          //axios.get(`${API_BASE_URL}/api/BusinessOnboarding/certifications-by-professions?professionIds=${professionIds.join(',')}`),
          //axios.get(`${API_BASE_URL}/api/BusinessOnboarding/qualifications-by-professions?professionIds=${professionIds.join(',')}`),
          //axios.get(`${API_BASE_URL}/api/BusinessOnboarding/memberships-by-professions?professionIds=${professionIds.join(',')}`)

          axios.get(`${API_BASE_URL}/api/BusinessOnboarding/certifications-by-professions?${queryString}`),
          axios.get(`${API_BASE_URL}/api/BusinessOnboarding/qualifications-by-professions?${queryString}`),
          axios.get(`${API_BASE_URL}/api/BusinessOnboarding/memberships-by-professions?${queryString}`)

        ]);
        
        setAvailableCertifications(certs.data);
        setAvailableQualifications(quals.data);
        setAvailableMemberships(memberships.data);

      } catch (err) {
        showToast('error', 'Could not load data for the selected professions.');
        console.error("Error fetching profession-dependent data:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfessionDependentData();

  }, [selectedProfessions]);




  // Address search
  const handleAddressSearchChange = async (e) => {
    const searchValue = e.target.value;
    setCurrentAddressInput(searchValue);
    if (searchValue.length > 2) {
      setIsSearchingAddress(true);
      try {
        const response = await axios.get(`${API_BASE_URL}/api/GooglePlaces/addresscomplete`, { params: { input: searchValue } });
        setAddressResults(response.data?.predictions || []);
      } catch (error) {
        console.error('Error fetching address suggestions:', error);
      } finally {
        setIsSearchingAddress(false);
      }
    } else {
      setAddressResults([]);
    }
  };

  const handleSelectAddress = (suggestion) => {
    setCurrentAddressInput(suggestion.description);
    setAddressResults([]);
  };

  const handleAddAddress = () => {
    if (!currentAddressInput.trim()) {
      showToast('error', 'Please enter an address.');
      return;
    }
    const newAddress = {
      address: currentAddressInput.trim(),
      mobile: currentMobileInput.trim(),
      landline: currentLandlineInput.trim(),
      isPrimary: addresses.length === 0,
    };

    let updatedAddresses = [...addresses, newAddress];
    if (newAddress.isPrimary && updatedAddresses.length > 1) {
        // This makes sure if the first address is added, any potential bug is cleared
        updatedAddresses = updatedAddresses.map((addr, index) => ({
            ...addr,
            isPrimary: index === updatedAddresses.length -1 // makes the new one primary
        }));
      }



    setAddresses([...addresses, newAddress]);
    setCurrentAddressInput('');
    setCurrentMobileInput('');
    setCurrentLandlineInput('');
  };

const handleSetPrimary = (indexToMakePrimary) => {
  const updatedAddresses = addresses.map((addr, index) => ({
    ...addr,
    // Set isPrimary to true for the selected address, false for all others
    isPrimary: index === indexToMakePrimary,
  }));
  setAddresses(updatedAddresses);
};

// UPDATED FUNCTION: Now handles removing the primary address gracefully
const handleRemoveAddress = (indexToRemove) => {
  // Find out if the address being removed is the primary one
  const isRemovingPrimary = addresses[indexToRemove]?.isPrimary;

  // Filter out the address from the array
  let updatedAddresses = addresses.filter((_, i) => i !== indexToRemove);

  // If we removed the primary address AND there are still addresses left...
  if (isRemovingPrimary && updatedAddresses.length > 0) {
    // ...make the new first address in the list the primary one.
    updatedAddresses[0].isPrimary = true;
  }

  setAddresses(updatedAddresses);
};

  // Generic search handler
  const createSearchHandler = (endpoint, setSearchResults) => async (searchTerm) => {
    if (searchTerm.length > 0) {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/${endpoint}?search=${searchTerm}`);
        setSearchResults(response.data.slice(0, 30));
      } catch (error) {
        console.error(`Error fetching ${endpoint}:`, error);
      }
    } else {
      setSearchResults([]);
    }
  };

  const handleProfessionSearch = async (searchTerm) => {
    setProfessionSearchTerm(searchTerm);
    if (searchTerm.length > 0) {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/ProfessionSearch/searchprofessions?search=${searchTerm}`);
        setProfessionResults(response.data);
      } catch (error) {
        console.error(`Error fetching professions:`, error);
      }
    } else {
      setProfessionResults([]);
    }
  };

  const handleServiceSearch = async (searchTerm) => {
    setServiceSearchTerm(searchTerm);
    if (searchTerm.length > 0) {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/Search/searchservices?search=${searchTerm}`);
        setServiceResults(response.data);
      } catch (error) {
        console.error(`Error fetching services:`, error);
      }
    } else {
      setServiceResults([]);
    }
  };

  const handleProductSearch = async (searchTerm) => {
    setProductSearchTerm(searchTerm);
    if (searchTerm.length > 0) {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/Search/searchproducts?search=${searchTerm}`);
        setProductResults(response.data);
      } catch (error) {
        console.error(`Error fetching products:`, error);
      }
    } else {
      setProductResults([]);
    }
  };

  const handleSelectProfession = async (item) => {
    if (selectedProfessions.some((i) => i.id === item.id)) {
      return; // Avoid re-adding and re-fetching
    }

    setSelectedProfessions(prev => [...prev, item]);
    setProfessionSearchTerm('');
    setProfessionResults([]);

    try {
      const response = await axios.get(`${API_BASE_URL}/api/ProfessionSearch/servicesByProfession/${item.id}`);
      const services = response.data;
      
      if (services && services.length > 0) {
        // Store the mapping of profession to services
        setProfessionServicesMap(prevMap => ({
          ...prevMap,
          [item.id]: services.map(s => s.id)
        }));

        // Add new services, avoiding duplicates
        setSelectedServices(prevServices => {
          const existingServiceIds = new Set(prevServices.map(s => s.id || s.serviceId));
          const newServices = services.filter(s => !existingServiceIds.has(s.id));
          if (newServices.length > 0) {
            setShowServicesMessage(true);
          }
          return [...prevServices, ...newServices];
        });
      }
    } catch (error) {
      console.error(`Error fetching services for profession ${item.id}:`, error);
      showToast('error', 'Could not load services for the selected profession.');
    }
  };

  const handleSelectService = (item) => {
    if (!selectedServices.some((i) => (i.id || i.serviceId) === item.id)) {
      setSelectedServices([...selectedServices, item]);
    }
    setServiceSearchTerm('');
    setServiceResults([]);
  };

  const handleSelectProduct = (item) => {
    if (!selectedProducts.some((i) => i.id === item.id)) {
      setSelectedProducts([...selectedProducts, item]);
    }
    setProductSearchTerm('');
    setProductResults([]);
  };

  const handleRemoveService = (serviceIdToRemove) => {
    setSelectedServices(prev => prev.filter(service => (service.id || service.serviceId) !== serviceIdToRemove));
  };

  const handleClearAllServices = () => {
    Swal.fire({
      title: 'Are you sure?',
      text: "This will remove all selected services. You will have to re-select them if you change your mind.",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, clear all!'
    }).then((result) => {
      if (result.isConfirmed) {
        setSelectedServices([]);
        setShowServicesMessage(false);
        Swal.fire(
          'Cleared!',
          'All services have been removed.',
          'success'
        )
      }
    })
  };

  const handleRemoveProduct = (productId) => {
    setSelectedProducts(prev => prev.filter(p => p.id !== productId));
  };

  const handleRemoveProfession = (professionId) => {
    // Get the list of service IDs associated with this profession
    const servicesToRemove = professionServicesMap[professionId] || [];

    // Remove the services from the selectedServices state
    if (servicesToRemove.length > 0) {
      setSelectedServices(prevServices =>
        prevServices.filter(service => !servicesToRemove.includes(service.id || service.serviceId))
      );
    }

    // Remove the profession from the selectedProfessions state
    setSelectedProfessions(prevProfessions =>
      prevProfessions.filter(p => (p.professionId || p.id) !== professionId)
    );

    // Clean up the map
    setProfessionServicesMap(prevMap => {
      const newMap = { ...prevMap };
      delete newMap[professionId];
      if (Object.keys(newMap).length === 0) {
        setShowServicesMessage(false);
      }
      return newMap;
    });
  };

  const handleImageUpload = (e) => {
    if (e.target.files[0]) {
      const file = e.target.files[0];
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfileImageBase64(reader.result);
      };
      reader.readAsDataURL(file);
      setPreview(URL.createObjectURL(file));
      setProfileImage(file);
    }
  };

  const handleCertificationChange = (id) => {
    setSelectedCertifications((prev) =>
      prev.includes(id) ? prev.filter((cId) => cId !== id) : [...prev, id]
    );
  };

  const handleQualificationChange = (id) => {
    setSelectedQualifications((prev) =>
      prev.includes(id) ? prev.filter((qId) => qId !== id) : [...prev, id]
    );
  };

  const handleMembershipChange = (id) => {
    setSelectedMemberships((prev) =>
      prev.includes(id) ? prev.filter((mId) => mId !== id) : [...prev, id]
    );
  };

  const handleSubmit = async () => {
    setIsLoading(true);

    try {
      // Step 1: Save locations
      try {
        const locationsPayload = {
          businessId,
          locations: addresses.map(addr => ({
            address: addr.address,
            mobile: addr.mobile,
            landline: addr.landline,
            isPrimary: !!addr.isPrimary,
          }))
        };
        await axios.post(`${API_BASE_URL}/api/BusinessOnboarding/saveBusinessLocations`, locationsPayload);
      } catch (err) {
        showToast('error', 'Failed to save locations. Please check your address details.');
        console.error("Error saving locations:", err);
        setIsLoading(false);
        return;
      }

      // Step 2: Update business details
      try {
        await axios.post(`${API_BASE_URL}/api/BusinessOnboarding/updateBusinessOnboarding02`, {
          businessUserId,
          website: businessWebsite,
          acn,
          abn,
          profileImage: profileImageBase64,
          description: shortBio,
          profileDescription: longBio
        });
      } catch (err) {
        showToast('error', 'Failed to save business details.');
        console.error("Error saving business details:", err);
        setIsLoading(false);
        return;
      }

      // Step 3: Save scope and professions
      try {
        await axios.post(`${API_BASE_URL}/api/BusinessOnboarding/saveBusinessOnboarding03`, {
          businessUserId,
          businesstype: businessType,
          residential: scope.residential ? 1 : 0,
          commercial: scope.commercial ? 1 : 0,
          government: scope.government ? 1 : 0,
          afterHours: serviceAfterHours ? 1 : 0,
          weekends: serviceOnWeekends ? 1 : 0,
          professionIds: selectedProfessions.map(p => p.professionId || p.id).filter(id => id != null),
          ancillaryIds: []
        });
      } catch (err) {
        showToast('error', 'Failed to save professions. Please check your selections.');
        console.error("Error saving professions:", err);
        setIsLoading(false);
        return;
      }

      // Step 4: Save services
      try {
        await axios.post(`${API_BASE_URL}/api/BusinessOnboarding/savebusinessservices`, {
          businessUserId,
          serviceIds: selectedServices.map(s => s.serviceId || s.id).filter(id => id != null)
        });
      } catch (err) {
        showToast('error', 'Failed to save services.');
        console.error("Error saving services:", err);
        setIsLoading(false);
        return;
      }

      // Step 5: Save products
      try {
        await axios.post(`${API_BASE_URL}/api/BusinessOnboarding/savebusinessproducts`, {
          businessUserId,
          productIds: selectedProducts.map(p => p.id).filter(id => id != null)
        });
      } catch (err) {
        showToast('error', 'Failed to save products.');
        console.error("Error saving products:", err);
        setIsLoading(false);
        return;
      }

      // Step 6: Save accreditation
      try {

        //console.log(selectedCertifications);

        await axios.post(`${API_BASE_URL}/api/BusinessOnboarding/saveaccreditation`, {
          businessUserId,
          licenseNumber,
          licenseExpiry: licenseExpiry || null,
          fullyInsured: isFullyInsured,
          certificationIds: selectedCertifications,
        });
      } catch (err) {
        showToast('error', 'Failed to save accreditation details.');
        console.error("Error saving accreditation:", err);
        setIsLoading(false);
        return;
      }

      // Step 7: Save qualifications and memberships
      try {
        await axios.post(`${API_BASE_URL}/api/BusinessOnboarding/saveQualificationsAndMemberships`, {
          businessUserId,
          registrationNumber,
          yearsExperience: parseInt(yearsExperience) || 1,
          qualificationIds: selectedQualifications,
          membershipIds: selectedMemberships,
        });
      } catch (err) {
        showToast('error', 'Failed to save qualifications and memberships.');
        console.error("Error saving qualifications:", err);
        setIsLoading(false);
        return;
      }

      // Final step: Mark onboarding as complete
      try {
        console.log(`businessUserId = ${businessUserId}`)
        const response = await axios.post(`${API_BASE_URL}/api/BusinessOnboarding/updateOnboardingBusiness?businessUserId=${businessUserId}`);
        if (response.status === 200) {
          Swal.fire({
            title: "🎉 Congratulations!",
            text: "You've successfully completed the onboarding process!",
            icon: "success",
            confirmButtonText: "Go to Dashboard",
          }).then(() => {
            localStorage.setItem('shouldOnboard', "0");
            //const shouldOnboard = localStorage.getItem('shouldOnboard');
            navigate('/businessdash');
          });
        }
      } catch (err) {
        showToast('error', 'Failed to update onboarding status.');
        console.error("Error updating onboarding status:", err);
        setIsLoading(false);
        return;
      }

    } catch (err) {
      showToast('error', 'An unexpected error occurred. Please try again.');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={`${styles.jobDetailNew_minHeightScreen} ${styles.jobDetailNew_bgGradientPage}`}>
      <header className={styles.jobDetailNew_header}>
        <div className={`${styles.jobDetailNew_container} ${styles.jobDetailNew_headerContent}`}>
          <div className={styles.jobDetailNew_headerLeft}>
            {businessIdFromParams && (
              <button onClick={() => navigate(-1)} className={`${styles.jobDetailNew_button} ${styles.jobDetailNew_buttonGhost}`} style={{ color: 'white' }}>
                <ArrowLeft className={styles.jobDetailNew_iconMd} />
              </button>
            )}
            <div className={styles.jobDetailNew_headerLogo}>
              <Home className={`${styles.jobDetailNew_iconMd} ${styles.jobDetailNew_textAmber400}`} />
              <span className={styles.jobDetailNew_headerTitle}>Build Start</span>
            </div>
            <div className={styles.jobDetailNew_headerSeparator}></div>
            <div>
              <h1 className={styles.jobDetailNew_headerTitle}>{businessIdFromParams ? "Edit Business Profile" : "Business Onboarding"}</h1>
              <p className={styles.jobDetailNew_textBlue200}>{businessIdFromParams ? "Update your business details" : "Complete your profile to get started"}</p>
            </div>
          </div>
          <div className={styles.jobDetailNew_headerRight}>
            <button onClick={() => handleButtonClick('logout')} className={`${styles.jobDetailNew_button} ${styles.jobDetailNew_buttonGhost}`} style={{ color: 'white' }}>
              <LogOut className={styles.jobDetailNew_iconMd} />
              <span className={styles.jobDetailNew_ml2}>Logout</span>
            </button>
          </div>
        </div>
      </header>

      <div className={`${styles.jobDetailNew_container} ${styles.jobDetailNew_mainContent}`}>
        {isLoading && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999,
          }}>
            <Loader2 />
          </div>
        )}
        {error && <p style={{ color: 'red' }}>{error}</p>}
        <div className={`${styles.jobDetailNew_grid} ${styles.jobDetailNew_lg_gridCols3}`}>
          <div className={`${styles.jobDetailNew_lg_colSpan2} ${styles.jobDetailNew_spaceY6}`}>


            {/* --- Business Details Card --- */}
            <div className={styles.jobDetailNew_card}>
              <div className={`${styles.jobDetailNew_cardHeader} ${styles.jobDetailNew_cardHeaderGradient}`}>
                <h3 className={styles.jobDetailNew_cardTitle}>
                  <Briefcase className={styles.jobDetailNew_iconMd} />
                  <span>Business Details</span>
                </h3>
              </div>
              <div className={`${styles.jobDetailNew_cardContent} ${styles.jobDetailNew_cardContentSpaceY6}`}>
                <div className={`${styles.jobDetailNew_grid} ${styles.jobDetailNew_md_gridCols2}`} style={{gap: '1rem'}}>
                  <div>
                    <label htmlFor="businessName" className={styles.jobDetailNew_label}>Business Name</label>
                    <input id="businessName" type="text" placeholder="Your Business Name" value={businessName} className={styles.jobDetailNew_input} disabled />
                  </div>
                  <div>
                    <label htmlFor="businessWebsite" className={styles.jobDetailNew_label}>Business Website</label>
                    <div className={styles.jobDetailNew_inputWithIconContainer}>
                      <Link2 className={styles.jobDetailNew_inputIcon} />
                      <input id="businessWebsite" type="text" placeholder="https://example.com" value={businessWebsite} onChange={(e) => setBusinessWebsite(e.target.value)} className={`${styles.jobDetailNew_input} ${styles.jobDetailNew_inputPl10}`} disabled={isReadOnly} />
                    </div>
                  </div>
                </div>
                <div className={`${styles.jobDetailNew_grid} ${styles.jobDetailNew_md_gridCols2}`} style={{gap: '1rem'}}>
                  <div>
                    <label htmlFor="abn" className={styles.jobDetailNew_label}>ABN</label>
                    <input id="abn" type="text" placeholder="Your ABN" value={abn} onChange={(e) => setAbn(e.target.value)} className={styles.jobDetailNew_input} disabled={isReadOnly} />
                  </div>
                  <div>
                    <label htmlFor="acn" className={styles.jobDetailNew_label}>ACN (Optional)</label>
                    <input id="acn" type="text" placeholder="Your ACN" value={acn} onChange={(e) => setAcn(e.target.value)} className={styles.jobDetailNew_input} disabled={isReadOnly} />
                  </div>
                </div>
              </div>
            </div>

            {/* --- Describe Your Business Card --- */}
            <div className={styles.jobDetailNew_card}>
              <div className={`${styles.jobDetailNew_cardHeader} ${styles.jobDetailNew_cardHeaderGradient}`}>
                <h3 className={styles.jobDetailNew_cardTitle}>
                  <FileText className={styles.jobDetailNew_iconMd} />
                  <span>Describe Your Business</span>
                </h3>
              </div>
              <div className={`${styles.jobDetailNew_cardContent} ${styles.jobDetailNew_cardContentSpaceY6}`}>
                <div>
                  <label htmlFor="shortBio" className={styles.jobDetailNew_label}>Describe your business in one line</label>
                  <input id="shortBio" type="text" placeholder="e.g., Premier Quantity Surveying company with 10 years experience" value={shortBio} onChange={(e) => setShortBio(e.target.value)} className={styles.jobDetailNew_input} disabled={isReadOnly} />
                </div>
                <div>
                  <label htmlFor="longBio" className={styles.jobDetailNew_label}>Describe your business in detail</label>
                  <textarea id="longBio" placeholder="Provide a detailed description of your business..." value={longBio} onChange={(e) => setLongBio(e.target.value)} className={styles.jobDetailNew_textarea} rows={4} disabled={isReadOnly}></textarea>
                </div>
              </div>
            </div>



            {/* --- Business Profession --- */}
            <div className={styles.jobDetailNew_card}>
              <div className={`${styles.jobDetailNew_cardHeader} ${styles.jobDetailNew_cardHeaderGradient}`}>
                <h3 className={styles.jobDetailNew_cardTitle}>
                  <Briefcase className={styles.jobDetailNew_iconMd} />
                  <span>Business Profession</span>
                </h3>
              </div>
              <div className={`${styles.jobDetailNew_cardContent} ${styles.jobDetailNew_cardContentSpaceY6}`}>
                  
                <div>
                  <label className={styles.jobDetailNew_label}>Add one or more profession categories</label>
                  <div className={styles.jobDetailNew_inputWithIconContainer} ref={professionDropdownRef}>
                    <Search className={styles.jobDetailNew_inputIcon} />
                    <input
                      type="text"
                      placeholder="Search for professions..."
                      className={`${styles.jobDetailNew_input} ${styles.jobDetailNew_inputPl10}`}
                      value={professionSearchTerm}
                      onChange={(e) => {
                        setProfessionSearchTerm(e.target.value);
                        handleProfessionSearch(e.target.value);
                      }}
                      onFocus={() => !isReadOnly && setIsProfessionDropdownOpen(true)}
                      disabled={isReadOnly}
                    />
                    {isProfessionDropdownOpen && professionResults.length > 0 && (
                      <ul className={styles.jobDetailNew_dropdownResults}>
                        {professionResults.map((item) => (
                          <li key={item.id} className={styles.jobDetailNew_resultItem} onClick={() => handleSelectProfession(item)}>
                            {item.name}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div className={styles.jobDetailNew_flexWrap} style={{ marginTop: '1rem' }}>
                    {selectedProfessions.map((prof) => (
                      <span key={prof.professionId || prof.id} className={styles.jobDetailNew_badge}>
                        {prof.name}
                        {!isReadOnly && (
                          <button type="button" onClick={() => handleRemoveProfession(prof.professionId || prof.id)} className={`${styles.jobDetailNew_button} ${styles.jobDetailNew_buttonGhost} ${styles.jobDetailNew_buttonSm}`}>
                            <X className={styles.jobDetailNew_buttonIcon} />
                          </button>
                        )}
                      </span>
                    ))}
                  </div>
                </div>
                  


              </div>
            </div>
            {/* --- END Business Profession --- */}


            {/* --- Services & Products Card --- */}
            <div className={styles.jobDetailNew_card}>
              <div className={`${styles.jobDetailNew_cardHeader} ${styles.jobDetailNew_flexItemsCenter}`}>
                <h3 className={styles.jobDetailNew_cardTitle}>
                  <Briefcase className={styles.jobDetailNew_iconMd} />
                  <span>Services & Products</span>
                </h3>
                <div style={{ display: 'flex', gap: '1rem', marginLeft: 'auto' }}>
                  {!isReadOnly && (
                    <button
                      type="button"
                      onClick={() => setShowAddServicePopup(true)}
                      className={`${styles.jobDetailNew_button} ${styles.jobDetailNew_buttonPrimary}`}
                      style={{ backgroundColor: '#2563eb', color: 'white', paddingLeft: '1rem', paddingRight: '1rem' }}
                    >
                      <Plus className={`${styles.jobDetailNew_buttonIcon} ${styles.jobDetailNew_buttonIconMr2}`} />
                      Add Service
                    </button>
                  )}
                  {!isReadOnly && selectedServices.length > 0 && (
                    <button
                      type="button"
                      onClick={handleClearAllServices}
                      className={`${styles.jobDetailNew_button} ${styles.jobDetailNew_buttonOutline}`}
                      style={{ borderColor: '#ef4444', color: '#ef4444', paddingLeft: '1rem', paddingRight: '1rem' }}
                    >
                      <X className={`${styles.jobDetailNew_buttonIcon} ${styles.jobDetailNew_buttonIconMr2}`} />
                      Clear All
                    </button>
                  )}
                </div>
              </div>
              <div className={`${styles.jobDetailNew_cardContent} ${styles.jobDetailNew_cardContentSpaceY6}`}>
                {/* Services */}
                <div>
                  {showServicesMessage && (
                    <p style={{ color: 'red', marginBottom: '0.5rem' }}>
                      We have auto assigned the services below. Deselect any that do no apply
                    </p>
                  )}
                  <label className={styles.jobDetailNew_label}>What services do you provide?</label>
                  <div className={styles.jobDetailNew_inputWithIconContainer}>
                    <Search className={styles.jobDetailNew_inputIcon} />
                    <input
                      type="text"
                      placeholder="Search for services..."
                      className={`${styles.jobDetailNew_input} ${styles.jobDetailNew_inputPl10}`}
                      value={serviceSearchTerm}
                      onChange={(e) => {
                        setServiceSearchTerm(e.target.value);
                        handleServiceSearch(e.target.value);
                      }}
                      disabled={isReadOnly}
                    />
                     {serviceResults.length > 0 && (
                      <ul className={styles.jobDetailNew_dropdownResults}>
                        {serviceResults.map((item) => (
                          <li key={item.id} className={styles.jobDetailNew_resultItem} onClick={() => handleSelectService(item)}>
                            {item.name}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div className={styles.jobDetailNew_flexWrap} style={{ marginTop: '1rem' }}>
                    {selectedServices.map((service) => {
                      const serviceId = service.id || service.serviceId;
                      return (
                        <span key={serviceId} className={styles.jobDetailNew_badge}>
                          {service.name}
                          {!isReadOnly && (
                            <button type="button" onClick={() => handleRemoveService(serviceId)} className={`${styles.jobDetailNew_button} ${styles.jobDetailNew_buttonGhost} ${styles.jobDetailNew_buttonSm}`}>
                              <X className={styles.jobDetailNew_buttonIcon} />
                            </button>
                          )}
                        </span>
                      );
                    })}
                  </div>
                </div>
                {/* Products */}
                <div>
                  <label className={styles.jobDetailNew_label}>What products do you provide? (Optional)</label>
                  <div className={styles.jobDetailNew_inputWithIconContainer}>
                    <Search className={styles.jobDetailNew_inputIcon} />
                    <input
                      type="text"
                      placeholder="Search for products..."
                      className={`${styles.jobDetailNew_input} ${styles.jobDetailNew_inputPl10}`}
                      value={productSearchTerm}
                      onChange={(e) => {
                        setProductSearchTerm(e.target.value);
                        handleProductSearch(e.target.value);
                      }}
                      disabled={isReadOnly}
                    />
                     {productResults.length > 0 && (
                      <ul className={styles.jobDetailNew_dropdownResults}>
                        {productResults.map((item) => (
                          <li key={item.id} className={styles.jobDetailNew_resultItem} onClick={() => handleSelectProduct(item)}>
                            {item.name}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div className={styles.jobDetailNew_flexWrap} style={{ marginTop: '1rem' }}>
                    {selectedProducts.map((product) => (
                      <span key={product.id} className={styles.jobDetailNew_badge}>
                        {product.name}
                        {!isReadOnly && (
                          <button type="button" onClick={() => handleRemoveProduct(product.id)} className={`${styles.jobDetailNew_button} ${styles.jobDetailNew_buttonGhost} ${styles.jobDetailNew_buttonSm}`}>
                            <X className={styles.jobDetailNew_buttonIcon} />
                          </button>
                        )}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>




            {/* --- Locations Card --- */}
            <div className={styles.jobDetailNew_card}>
              <div className={`${styles.jobDetailNew_cardHeader} ${styles.jobDetailNew_cardHeaderGradient}`}>
                <h3 className={styles.jobDetailNew_cardTitle}>
                  <MapPin className={styles.jobDetailNew_iconMd} />
                  <span>Business Locations</span>
                </h3>
              </div>
              <div className={`${styles.jobDetailNew_cardContent} ${styles.jobDetailNew_cardContentSpaceY6}`}>
                {/* Display added addresses */}
                {addresses.length > 0 && (



                    <div className={styles.jobDetailNew_cardContentSpaceY4}>
                      <label className={styles.jobDetailNew_label}>Your Locations:</label>
                      <ul className={styles.jobDetailNew_cardContentSpaceY2} style={{ listStyle: 'none', padding: 0 }}>
                        {addresses.map((addr, index) => (
                          <li key={index} className={styles.jobDetailNew_addressListItem}>
                            
                            {/* --- Left Side: Address Info (No Changes Here) --- */}
                            <div className={styles.jobDetailNew_addressDetails}>
                              <MapPin size={20} className={styles.jobDetailNew_textBlue500} style={{ marginTop: '4px' }}/>
                              <div className={styles.jobDetailNew_addressText}>
                                <p>
                                  {addr.address}
                                  {addr.isPrimary && (
                                    <span className={styles.jobDetailNew_primaryBadge} style={{ marginLeft: '0.5rem' }}>Primary</span>
                                  )}
                                </p>
                                <div className={styles.jobDetailNew_addressContact}>
                                  {addr.mobile && <span><Phone size={14} /> {addr.mobile}</span>}
                                  {addr.landline && <span><Phone size={14} /> {addr.landline}</span>}
                                </div>
                              </div>
                            </div>

                            {/* --- Right Side: Action Buttons (CORRECTED LOGIC) --- */}
                            <div className={styles.jobDetailNew_addressActions}>
                              
                              {/* "Make Primary" button: Shows only for non-primary addresses */}
                              {!addr.isPrimary && !isReadOnly && (
                                <button 
                                  type="button" 
                                  onClick={() => handleSetPrimary(index)} 
                                  className={`${styles.jobDetailNew_button} ${styles.jobDetailNew_buttonOutline} ${styles.jobDetailNew_buttonSm}`}
                                >
                                  Make Primary
                                </button>
                              )}
                              
                              {/* "Remove" button: Shows for ALL addresses as long as there's more than one */}
                              {addresses.length > 1 && !isReadOnly && (
                                <button 
                                  type="button" 
                                  onClick={() => handleRemoveAddress(index)} 
                                  className={`${styles.jobDetailNew_button} ${styles.jobDetailNew_buttonGhost} ${styles.jobDetailNew_buttonSm}`}
                                  title="Remove Location"
                                >
                                  <X className={styles.jobDetailNew_buttonIcon} />
                                </button>
                              )}
                            </div>

                          </li>
                        ))}
                      </ul>
                    </div>


)}

                {/* Form to add a new address */}
                <div>
                  <label className={styles.jobDetailNew_label}>{addresses.length === 0 ? 'Add Primary Address' : 'Add Secondary Address'}</label>
                  <div className={`${styles.jobDetailNew_grid} ${styles.jobDetailNew_md_gridCols2}`} style={{gap: '1rem', marginBottom: '1rem'}}>
                    <div>
                      <label htmlFor="mobile" className={styles.jobDetailNew_label}>Mobile</label>
                      <input id="mobile" type="tel" placeholder="04xxxxxxxx" value={currentMobileInput} onChange={(e) => setCurrentMobileInput(e.target.value)} className={styles.jobDetailNew_input} disabled={isReadOnly} />
                    </div>
                    <div>
                      <label htmlFor="landline" className={styles.jobDetailNew_label}>Landline (Optional)</label>
                      <input id="landline" type="tel" placeholder="Your landline" value={currentLandlineInput} onChange={(e) => setCurrentLandlineInput(e.target.value)} className={styles.jobDetailNew_input} disabled={isReadOnly} />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="address" className={styles.jobDetailNew_label}>Address</label>
                    <div className={styles.jobDetailNew_inputWithIconContainer}>
                      <Search className={styles.jobDetailNew_inputIcon} />
                      <input id="address" type="text" placeholder="Search for an address" value={currentAddressInput} onChange={handleAddressSearchChange} className={`${styles.jobDetailNew_input} ${styles.jobDetailNew_inputPl10}`} disabled={isReadOnly} />
                    </div>
                    {addressResults.length > 0 && (
                        <ul className={styles.jobDetailNew_dropdownResults}>
                          {addressResults.map((suggestion) => (
                            <li key={suggestion.place_id} className={styles.jobDetailNew_resultItem} onClick={() => handleSelectAddress(suggestion)}>
                              {suggestion.description}
                            </li>
                          ))}
                        </ul>
                      )}
                  </div>
                  {!isReadOnly && (
                    <button type="button" onClick={handleAddAddress} className={`${styles.jobDetailNew_button} ${styles.jobDetailNew_buttonOutline}`} style={{marginTop: '1rem'}}>
                      <Plus className={`${styles.jobDetailNew_buttonIcon} ${styles.jobDetailNew_buttonIconMr2}`} />
                      {addresses.length === 0 ? 'Set Primary Address' : 'Add Secondary Address'}
                    </button>
                  )}
                </div>
              </div>
            </div>




            {/* --- Scope of Work & Professions Card --- */}
            <div className={styles.jobDetailNew_card}>
              <div className={styles.jobDetailNew_cardHeader}>
                <h3 className={styles.jobDetailNew_cardTitle}>
                  <Target className={styles.jobDetailNew_iconMd} />
                  <span>Scope & Professions</span>
                </h3>
              </div>
              <div className={`${styles.jobDetailNew_cardContent} ${styles.jobDetailNew_cardContentSpaceY6}`}>
                <div>
                  <label className={styles.jobDetailNew_label}>Select your profession type</label>
                  <div className={styles.jobDetailNew_timelineButtonGrid}>
                    {['Trade Professional', 'Building Professional', 'Supplier'].map((type) => (
                      <button
                        type="button"
                        key={type}
                        className={`${styles.jobDetailNew_button} ${styles.jobDetailNew_timelineButton} ${businessType === type ? styles.jobDetailNew_bgBlue500 : styles.jobDetailNew_buttonOutline}`}
                        onClick={() => !isReadOnly && setBusinessType(type)}
                        disabled={isReadOnly}
                      >
                        {type}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className={styles.jobDetailNew_label}>What is the scope of work you do?</label>
                  <div className={styles.jobDetailNew_timelineButtonGrid}>
                    {['residential', 'commercial', 'government'].map((s) => (
                      <button
                        type="button"
                        key={s}
                        className={`${styles.jobDetailNew_button} ${styles.jobDetailNew_timelineButton} ${scope[s] ? styles.jobDetailNew_bgGreen500 : styles.jobDetailNew_buttonOutline}`}
                        onClick={() => !isReadOnly && setScope(prev => ({ ...prev, [s]: !prev[s] }))}
                        disabled={isReadOnly}
                      >
                        {s.charAt(0).toUpperCase() + s.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>
                
                <div>
                  <label className={styles.jobDetailNew_label}>Service Availability</label>
                  <div className={styles.jobDetailNew_flexItemsCenter} style={{gap: '2rem'}}>
                    <div className={styles.jobDetailNew_flexItemsCenter}>
                      <button type="button" onClick={() => !isReadOnly && setServiceAfterHours(!serviceAfterHours)} className={`${styles.jobDetailNew_switch} ${serviceAfterHours ? styles.jobDetailNew_switchActive : ''}`} disabled={isReadOnly}>
                        <span className={styles.jobDetailNew_switchToggle}></span>
                      </button>
                      <span className={styles.jobDetailNew_ml3}>Service After hours?</span>
                    </div>
                    <div className={styles.jobDetailNew_flexItemsCenter}>
                      <button type="button" onClick={() => !isReadOnly && setServiceOnWeekends(!serviceOnWeekends)} className={`${styles.jobDetailNew_switch} ${serviceOnWeekends ? styles.jobDetailNew_switchActive : ''}`} disabled={isReadOnly}>
                        <span className={styles.jobDetailNew_switchToggle}></span>
                      </button>
                      <span className={styles.jobDetailNew_ml3}>Service on Weekends?</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>



            {/* --- Accreditation & Insurance Card --- */}
            <div className={styles.jobDetailNew_card}>
              <div className={styles.jobDetailNew_cardHeader}>
                <h3 className={styles.jobDetailNew_cardTitle}>
                  <ShieldCheck className={styles.jobDetailNew_iconMd} />
                  <span>Accreditation & Insurance</span>
                </h3>
              </div>
              <div className={`${styles.jobDetailNew_cardContent} ${styles.jobDetailNew_cardContentSpaceY6}`}>
                <div className={`${styles.jobDetailNew_grid} ${styles.jobDetailNew_md_gridCols2}`} style={{gap: '1rem'}}>
                  <div>
                    <label htmlFor="licenseNumber" className={styles.jobDetailNew_label}>License Number</label>
                    <input id="licenseNumber" type="text" placeholder="Your license number" value={licenseNumber} onChange={(e) => setLicenseNumber(e.target.value)} className={styles.jobDetailNew_input} disabled={isReadOnly} />
                  </div>
                  <div>
                    <label htmlFor="licenseExpiry" className={styles.jobDetailNew_label}>Expiry Date (Optional)</label>
                    <input id="licenseExpiry" type="date" value={licenseExpiry} onChange={(e) => setLicenseExpiry(e.target.value)} className={styles.jobDetailNew_input} disabled={isReadOnly} />
                  </div>
                </div>
                <div>
                  <label className={styles.jobDetailNew_label}>Certifications</label>
                  <div className={`${styles.jobDetailNew_grid} ${styles.jobDetailNew_md_gridCols2}`} style={{gap: '1rem'}}>
                    {availableCertifications.length > 0 ? availableCertifications.map(cert => (
                      <div key={cert.certificationId} className={styles.jobDetailNew_checkboxContainer}>
                        <input type="checkbox" id={`cert-${cert.certificationId}`} checked={selectedCertifications.includes(cert.certificationId)} onChange={() => handleCertificationChange(cert.certificationId)} disabled={isReadOnly} />
                        <label htmlFor={`cert-${cert.certificationId}`}>{cert.name}</label>
                      </div>
                    )) : <p className={`${styles.jobDetailNew_textSm} ${styles.jobDetailNew_textGray600}`}>No certifications available.</p>}
                  </div>
                </div>
                <div>
                  <label className={styles.jobDetailNew_label}>Are you fully insured?</label>
                  <div className={styles.jobDetailNew_flexItemsCenter}>
                    <button
                      type="button"
                      onClick={() => !isReadOnly && setIsFullyInsured(!isFullyInsured)}
                      className={`${styles.jobDetailNew_switch} ${isFullyInsured ? styles.jobDetailNew_switchActive : ''}`}
                      disabled={isReadOnly}
                    >
                      <span className={styles.jobDetailNew_switchToggle}></span>
                    </button>
                    <span className={styles.jobDetailNew_ml3}>{isFullyInsured ? 'Yes, I am fully insured' : 'No, I am not fully insured'}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* --- Qualifications & Memberships Card --- */}
            <div className={styles.jobDetailNew_card}>
              <div className={styles.jobDetailNew_cardHeader}>
                <h3 className={styles.jobDetailNew_cardTitle}>
                  <Award className={styles.jobDetailNew_iconMd} />
                  <span>Qualifications & Memberships</span>
                </h3>
              </div>
              <div className={`${styles.jobDetailNew_cardContent} ${styles.jobDetailNew_cardContentSpaceY6}`}>
                <div className={`${styles.jobDetailNew_grid} ${styles.jobDetailNew_md_gridCols2}`} style={{gap: '1rem'}}>
                  <div>
                    <label htmlFor="registrationNumber" className={styles.jobDetailNew_label}>Registration Number</label>
                    <input id="registrationNumber" type="text" placeholder="Your registration number" value={registrationNumber} onChange={(e) => setRegistrationNumber(e.target.value)} className={styles.jobDetailNew_input} disabled={isReadOnly} />
                  </div>
                  <div>
                    <label htmlFor="yearsExperience" className={styles.jobDetailNew_label}>Years of Experience</label>
                    <input id="yearsExperience" type="number" min="0" placeholder="e.g., 5" value={yearsExperience} onChange={(e) => setYearsExperience(e.target.value)} className={styles.jobDetailNew_input} disabled={isReadOnly} />
                  </div>
                </div>
                <div>
                  <label className={styles.jobDetailNew_label}>Qualifications</label>
                  <div className={`${styles.jobDetailNew_grid} ${styles.jobDetailNew_md_gridCols2}`} style={{gap: '1rem'}}>
                    {availableQualifications.length > 0 ? availableQualifications.map(qual => (
                      <div key={qual.qualificationId} className={styles.jobDetailNew_checkboxContainer}>
                        <input type="checkbox" id={`qual-${qual.qualificationId}`} checked={selectedQualifications.includes(qual.qualificationId)} onChange={() => handleQualificationChange(qual.qualificationId)} disabled={isReadOnly} />
                        <label htmlFor={`qual-${qual.qualificationId}`}>{qual.name}</label>
                      </div>
                    )) : <p className={`${styles.jobDetailNew_textSm} ${styles.jobDetailNew_textGray600}`}>No qualifications available.</p>}
                  </div>
                </div>

{/*
                <div>
                  <label className={styles.jobDetailNew_label}>Memberships</label>
                  <div className={`${styles.jobDetailNew_grid} ${styles.jobDetailNew_md_gridCols2}`} style={{gap: '1rem'}}>
                    {availableMemberships.length > 0 ? availableMemberships.map(mem => (
                      <div key={mem.membershipId} className={styles.jobDetailNew_checkboxContainer}>
                        <input type="checkbox" id={`mem-${mem.membershipId}`} checked={selectedMemberships.includes(mem.membershipId)} onChange={() => handleMembershipChange(mem.membershipId)} disabled={isReadOnly} />
                        <label htmlFor={`mem-${mem.membershipId}`}>{mem.name}</label>
                      </div>
                    )) : <p className={`${styles.jobDetailNew_textSm} ${styles.jobDetailNew_textGray600}`}>No memberships available.</p>}
                  </div>
                </div>
*/}


              </div>
            </div>

            {/* --- Document Upload Card --- */}
            <div className={styles.jobDetailNew_card}>
              <div className={styles.jobDetailNew_cardHeader}>
                <h3 className={styles.jobDetailNew_cardTitle}>
                  <Upload className={styles.jobDetailNew_iconMd} />
                  <span>Upload Documents</span>
                </h3>
              </div>
              <div className={`${styles.jobDetailNew_cardContent} ${styles.jobDetailNew_cardContentSpaceY4}`}>
                <p className={`${styles.jobDetailNew_textSm} ${styles.jobDetailNew_textGray600}`}>Upload any relevant documents (e.g., licenses, certifications).</p>
                <div className={styles.jobDetailNew_uploadArea}>
                  <Upload className={styles.jobDetailNew_uploadIcon} />
                  <p className={`${styles.jobDetailNew_textGray600} ${styles.jobDetailNew_mb2}`}>Drag and drop a file here</p>
                  <p className={`${styles.jobDetailNew_textSm} ${styles.jobDetailNew_textGray500} ${styles.jobDetailNew_mb4}`}>or</p>
                  <label htmlFor="document-upload" className={`${styles.jobDetailNew_button} ${styles.jobDetailNew_buttonOutline}`} style={{cursor: isReadOnly ? 'default' : 'pointer'}}>
                    Browse File
                    <input id="document-upload" type="file" className="hidden" disabled={isReadOnly} />
                  </label>
                </div>
                {/* Document preview will be added here */}
              </div>
            </div>

            {/* --- Profile Image Card --- */}
            <div className={styles.jobDetailNew_card}>
              <div className={styles.jobDetailNew_cardHeader}>
                <h3 className={styles.jobDetailNew_cardTitle}>
                  <Camera className={`${styles.jobDetailNew_iconMd} ${styles.jobDetailNew_textAmber600}`} />
                  <span>Profile Image</span>
                </h3>
              </div>
              <div className={`${styles.jobDetailNew_cardContent} ${styles.jobDetailNew_cardContentSpaceY4}`}>
                <p className={`${styles.jobDetailNew_textSm} ${styles.jobDetailNew_textGray600}`}>Upload a logo or profile picture for your business.</p>
                <div className={styles.jobDetailNew_uploadArea}>
                  <Upload className={styles.jobDetailNew_uploadIcon} />
                  <p className={`${styles.jobDetailNew_textGray600} ${styles.jobDetailNew_mb2}`}>Drag and drop a file here</p>
                  <p className={`${styles.jobDetailNew_textSm} ${styles.jobDetailNew_textGray500} ${styles.jobDetailNew_mb4}`}>or</p>
                  <label htmlFor="profile-image-upload" className={`${styles.jobDetailNew_button} ${styles.jobDetailNew_buttonOutline}`} style={{cursor: isReadOnly ? 'default' : 'pointer'}}>
                    Browse File
                    <input id="profile-image-upload" type="file" accept="image/*" className="hidden" onChange={handleImageUpload} disabled={isReadOnly} />
                  </label>
                </div>
                {preview && (
                  <div className={styles.jobDetailNew_uploadedImagesGrid}>
                    <div className={styles.jobDetailNew_uploadedImageItem}>
                      <img src={preview} alt="Profile Preview" className={styles.jobDetailNew_uploadedImage} />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className={styles.jobDetailNew_spaceY6}>
            <div className={`${styles.jobDetailNew_card} ${styles.jobDetailNew_sidebarCard}`}>
              <div className={`${styles.jobDetailNew_cardHeader} ${styles.jobDetailNew_sidebarHeader}`}>
                <h3 className={styles.jobDetailNew_sidebarTitle}>Onboarding Summary</h3>
              </div>
              <div className={`${styles.jobDetailNew_cardContent} ${styles.jobDetailNew_sidebarContentSpaceY4}`}>
                <div className={styles.jobDetailNew_sidebarContentSpaceY3}>
                  <div>
                    <label className={styles.jobDetailNew_sidebarLabel}>BUSINESS NAME</label>
                    <p className={styles.jobDetailNew_sidebarValue}>{businessName || "Not specified"}</p>
                  </div>
                  <div>
                    <label className={styles.jobDetailNew_sidebarLabel}>LOCATIONS</label>
                    <p className={styles.jobDetailNew_sidebarValue}>{addresses.length} added</p>
                  </div>
                  <div>
                    <label className={styles.jobDetailNew_sidebarLabel}>PROFESSIONS</label>
                    <p className={styles.jobDetailNew_sidebarValue}>{selectedProfessions.length} selected</p>
                  </div>
                  <div>
                    <label className={styles.jobDetailNew_sidebarLabel}>SERVICES</label>
                    <p className={styles.jobDetailNew_sidebarValue}>{selectedServices.length} selected</p>
                  </div>
                  <div>
                    <label className={styles.jobDetailNew_sidebarLabel}>PRODUCTS</label>
                    <p className={styles.jobDetailNew_sidebarValue}>{selectedProducts.length} selected</p>
                  </div>
                </div>
                <div className={styles.jobDetailNew_separator}></div>
                <div className={styles.jobDetailNew_sidebarContentSpaceY3}>
                  {!isReadOnly && (
                    <button type="button" onClick={handleSubmit} className={`${styles.jobDetailNew_button} ${styles.jobDetailNew_buttonFullWidth} ${styles.jobDetailNew_buttonPrimary}`}>
                      <ShieldCheck className={`${styles.jobDetailNew_iconBase} ${styles.jobDetailNew_buttonIconMr2}`} />
                      {businessIdFromParams ? "Save Changes" : "Complete Onboarding"}
                    </button>
                  )}
                  {businessIdFromParams && (
                    <button type="button" onClick={() => navigate(-1)} className={`${styles.jobDetailNew_button} ${styles.jobDetailNew_buttonFullWidth} ${styles.jobDetailNew_buttonOutline}`}>
                      <ArrowLeft className={`${styles.jobDetailNew_iconBase} ${styles.jobDetailNew_buttonIconMr2}`} />
                      Go Back
                    </button>
                  )}
                </div>
              </div>
            </div>
            <div className={`${styles.jobDetailNew_card} ${styles.jobDetailNew_tipsCard}`}>
              <div className={styles.jobDetailNew_tipsContent}>
                <div className={styles.jobDetailNew_tipsEmoji}>💡</div>
                <h3 className={styles.jobDetailNew_tipsTitle}>Pro Tips</h3>
                <ul className={styles.jobDetailNew_tipsList}>
                  <li>• A complete profile attracts more clients.</li>
                  <li>• Keep your license and insurance details up to date.</li>
                  <li>• Showcase your best work with a great profile image.</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showAddServicePopup && (
        <div style={modalBackdropStyle}>
          <div style={modalContentStyle}>
            <div style={modalHeaderStyle}>
              <h3 className={styles.jobDetailNew_cardTitle}>Add a New Service</h3>
              <button onClick={() => setShowAddServicePopup(false)} className={`${styles.jobDetailNew_button} ${styles.jobDetailNew_buttonGhost}`}>
                <X />
              </button>
            </div>
            <div className={`${styles.jobDetailNew_cardContent} ${styles.jobDetailNew_cardContentSpaceY6}`}>
              <div>
                <label htmlFor="profession-select" className={styles.jobDetailNew_label}>
                  Which profession does this service belong to?
                </label>
                <select
                  id="profession-select"
                  value={newService.professionId}
                  onChange={(e) => setNewService({ ...newService, professionId: e.target.value })}
                  className={styles.jobDetailNew_input}
                >
                  <option value="">Select a profession</option>
                  {selectedProfessions.map(prof => (
                    <option key={prof.id || prof.professionId} value={prof.id || prof.professionId}>
                      {prof.name}
                    </option>
                  ))}
                </select>
                 {selectedProfessions.length === 0 && (
                    <p className={`${styles.jobDetailNew_textSm} ${styles.jobDetailNew_textGray600}`} style={{marginTop: '0.5rem'}}>
                        Please add a profession first before adding a custom service.
                    </p>
                )}
              </div>
              <div>
                <label htmlFor="new-service-name" className={styles.jobDetailNew_label}>
                  New Service Name
                </label>
                <input
                  id="new-service-name"
                  type="text"
                  placeholder="e.g., Custom Cabinetry"
                  value={newService.name}
                  onChange={(e) => setNewService({ ...newService, name: e.target.value })}
                  className={styles.jobDetailNew_input}
                />
              </div>
            </div>
            <div style={modalFooterStyle}>
              <button
                type="button"
                onClick={() => setShowAddServicePopup(false)}
                className={`${styles.jobDetailNew_button} ${styles.jobDetailNew_buttonOutline}`}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleAddNewService}
                className={`${styles.jobDetailNew_button} ${styles.jobDetailNew_buttonPrimary}`}
                disabled={addServiceLoading}
              >
                {addServiceLoading ? <Loader2 /> : 'Add Service'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const modalBackdropStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 1000,
};

const modalContentStyle = {
  backgroundColor: 'white',
  padding: '2rem',
  borderRadius: '8px',
  width: '90%',
  maxWidth: '500px',
  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
};

const modalHeaderStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  borderBottom: '1px solid #e5e7eb',
  paddingBottom: '1rem',
  marginBottom: '1rem',
};

const modalFooterStyle = {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '1rem',
  paddingTop: '1rem',
  marginTop: '1rem',
  borderTop: '1px solid #e5e7eb',
};

export default BusinessOnboardingNew;
